import { useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";

// Der aufgeklappte Bearbeitungskasten in der Aufteilung: Felder in einem
// Kasten, darunter die Aktionen. Optik und Geometrie sind die von
// `EditableRow` (components/design), damit Bearbeiten hier aussieht wie
// Bearbeiten im Profil.
//
// Warum nicht `EditPanelButtons` aus demselben Baustein: dessen dritter Knopf
// heisst "Abbrechen" und bedeutet dort "verwirf meinen Entwurf, gespeichert
// wird erst mit der SaveBar" (@adr SPARK-0005-unified-deferred-save-bar@1).
// Diese Seite speichert automatisch (@adr SPARK-0008-match-editor-autosave@1),
// hier ist beim Klick längst geschrieben. Ein Abbrechen, das nichts zurücknimmt,
// wäre eine Lüge, also gibt es ihn nicht.
//
// Gelöscht wird in zwei Schritten
// (@adr SPARK-0011-destructive-actions-require-confirmation@1): der erste Klick
// bewaffnet, der zweite löscht. Ein Dialog wäre für eine Zeile in einer Liste zu
// schwer, und der erste Klick darf nie schon zerstören.

export function ProjectEditBox({
  children,
  onDone,
  onDelete,
  deleteLabel,
  confirmQuestion,
  idPrefix,
}: {
  children: ReactNode;
  onDone: () => void;
  onDelete: () => void;
  /** Beschriftung des ruhenden Löschknopfes, etwa "Paket löschen". */
  deleteLabel: string;
  /** Die Frage im bewaffneten Zustand - sie benennt, was verloren geht. */
  confirmQuestion: string;
  /** Testanker: `${idPrefix}-done`, `-delete`, `-delete-question`, `-delete-confirm`. */
  idPrefix?: string;
}) {
  const intl = useIntl();
  const [armed, setArmed] = useState(false);

  return (
    <div className="space-y-3 rounded-md bg-page p-3 ring-1 ring-ink/10">
      {children}
      {/* Beide Knöpfe links, Fertig zuerst: der Blick muss nicht quer über die
          Zeile wandern, und das Zerstörerische liegt nicht dort, wo die Hand beim
          Schliessen ohnehin hinfährt. */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button id={idPrefix && `${idPrefix}-done`} variant="primary" size="compact" onClick={onDone}>
          <FormattedMessage id="project.plan.done" defaultMessage="Fertig" />
        </Button>
        {armed ? (
          <div className="flex flex-wrap items-center gap-2">
            <span id={idPrefix && `${idPrefix}-delete-question`} className="smycles-field-meta text-ink">
              {confirmQuestion}
            </span>
            <Button id={idPrefix && `${idPrefix}-delete-confirm`} variant="destructive" size="compact" onClick={onDelete}>
              {intl.formatMessage({ id: "common.delete", defaultMessage: "Löschen" })}
            </Button>
            <Button variant="secondary" size="compact" onClick={() => setArmed(false)}>
              {intl.formatMessage({ id: "common.cancel", defaultMessage: "Abbrechen" })}
            </Button>
          </div>
        ) : (
          <Button id={idPrefix && `${idPrefix}-delete`} variant="secondary" size="compact" onClick={() => setArmed(true)}>
            {deleteLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
